import { EditIcon, Switch } from '@hc/ui/atoms';
import { MasterTable, MasterTopContainer } from '@hc/ui/components/backoffice';
import { SymptomsDrawer, TableSkeletonLoader } from '@hc/ui/components';
import { getDateFormatToString } from '@hc/dayjs';
import { useSymptoms } from '@hc/store//backoffice/masters/Symptoms';
import { Pagination } from '@hc/ui/components/backoffice/pagination';
import { queryClient } from '@hc/utils';
import { Box, Grid, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { SymptomMaster_Style } from './style';

export default function SymptomMaster(props) {
  const { ...rest } = props;

  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Store Data

  const {
    symptomsMasterData,
    symptomsMasterCount,
    getAllSymptoms,
    SymptomsStatusUpdate,
    addEditSymptoms,
    isIamValidToSave,
    initialEditSymptoms,
    clearSymptomsData,
    loading,
  } = useSymptoms((state) => ({
    symptomsMasterData: state.symptomsMasterData,
    symptomsMasterCount: state.symptomsMasterCount,
    getAllSymptoms: state.getAllSymptoms,
    SymptomsStatusUpdate: state.SymptomsStatusUpdate,
    addEditSymptoms: state.addEditSymptoms,
    isIamValidToSave: state.isIamValidToSave,
    initialEditSymptoms: state.initialEditSymptoms,
    clearSymptomsData: state.clearSymptomsData,
    loading: state.loading,
  }));

  //  ----- TABLE DATA SEARCH ---------
  const handleSearch = async (searchValue) => {
    setSearch(searchValue);
    await getAllSymptoms(searchValue, rowsPerPage);
    setOffset(0);
  };

  // ------TABLE DATA SWITCH --------
  const onStatusChange = async (val) => {
    const payload = {
      id: val.id ?? 0,
      active_status: !val?.is_active ?? true,
    };
    const response = await SymptomsStatusUpdate(payload);
    if (response === 200) {
      queryClient.invalidateQueries({
        queryKey: ['getAllsymptoms', search, offset],
      });
      await getAllSymptoms(search, rowsPerPage, offset);
    }
  };

  // ------PAGINATION ------------
  const onLimitChange = async (limit, offset) => {
    setRowsPerPage(limit);
    setOffset(offset);
    queryClient.invalidateQueries({
      queryKey: ['getAllSymptoms', search, offset],
    });
    await getAllSymptoms(search, limit, offset);
  };

  const onOffsetchange = async (offset) => {
    setOffset(offset);
    await getAllSymptoms(search, rowsPerPage, offset);
  };

  // ADD
  const upsertMaster = async () => {
    const result = isIamValidToSave();
    if (result) {
      const response = await addEditSymptoms();
      if (response?.status?.code === 200) {
        setDrawerOpen(false);
        clearSymptomsData();
        queryClient.invalidateQueries({
          queryKey: ['getAllsymptoms', search, offset],
        });
        await getAllSymptoms(search, rowsPerPage, offset);
      }
    }
  };

  const handleAddClick = () => {
    setDrawerOpen(true);
  };
  const closeDrugMasterDrawer = () => {
    setDrawerOpen(false);
    clearSymptomsData();
  };
  const onEdit = (val) => {
    initialEditSymptoms(val);
    setDrawerOpen(true);
  };

  //  TABLE COLUMNS META DATA
  const columns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 60,
    },
    {
      field: 'symptoms',
      headerName: 'Symptom',
      width: 180,
    },
    {
      field: 'status',
      headerName: 'Active/Inactive',
      width: 150,
      renderCell: (params) => (
        <Switch
          id="symptomStatus"
          checked={params?.row?.status}
          onChange={() => onStatusChange(params?.row?.value)}
          rootStyle={SymptomMaster_Style.switchSx}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 200,
      valueGetter: (params) =>
        getDateFormatToString(params.row.created_at, 'lll'),
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 200,
      valueGetter: (params) =>
        getDateFormatToString(params.row.updated_at, 'lll'),
    },
    {
      field: 'edit',
      headerName: 'Edit',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ pl: 1 }} onClick={() => onEdit(params?.row?.value)}>
          <EditIcon
            id="editSymptom"
            style={{ cursor: 'pointer', color: '#007965' }}
          />
        </Box>
      ),
    },
  ];

  //  INITIAL DATA FETCH

  const initialData = async () => {
    await getAllSymptoms('');
  };

  useEffect(() => {
    initialData();
  }, []);

  return (
    <Box
      {...rest}
      p={2}
      sx={{
        height: '100vh',
        overflowY: 'scroll',
        backgroundColor: '#fff',
        borderRadius: '10px',
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MasterTopContainer
            isMasterTitleShow
            isTitileShow={false}
            handleSearch={handleSearch}
            handleAddClick={handleAddClick}
            title="Symptom"
            count={symptomsMasterCount}
            showSearchField
            searchFieldPlaceholder="Search Symptom by name"
            showAddButton
            addButtonName="Add Symptom"
            addMasterStyle={{
              boxShadow:
                '0px 3px 1px -2px rgba(0,0,0,0.2),0px 3px 3px 0px rgba(0,0,0,0.14),0px 1px 2px 0px rgba(0,0,0,0.12)',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <TableSkeletonLoader />
          ) : (
            <Box>
              <MasterTable
                columns={columns}
                data={symptomsMasterData ?? []}
                customTableStyle={SymptomMaster_Style.dataTableCustomSx}
                hideFooter
                rootStyle={{ height: '68vh' }}
              />
            </Box>
          )}
          <Stack
            flexDirection="row"
            justifyContent="end"
            sx={{ width: '100%' }}
          >
            <Pagination
              totalCount={symptomsMasterCount}
              onLimitChange={onLimitChange}
              onOffsetchange={onOffsetchange}
              offset={offset}
              rowsPerPage={rowsPerPage}
            />
          </Stack>
        </Grid>
      </Grid>
      <SymptomsDrawer
        drawerOpen={drawerOpen}
        closeMasterDrawer={closeDrugMasterDrawer}
        upsertMaster={upsertMaster}
      />
    </Box>
  );
}

SymptomMaster.propTypes = {
  className: PropTypes.string,
};
